import { DOCUMENT } from '@angular/common';
import {
  Component,
  computed,
  effect,
  HostListener,
  inject,
  input,
  linkedSignal,
  OnDestroy,
  OnInit,
  output,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import {
  GcDropdownButtonComponent,
  GcListItemComponent,
  GcModalComponent,
  GcTabComponent,
  GcTabsComponent,
  IpoLanguageService,
  IpoTranslatePipe,
} from '@ip-only/front-end-library';
import {
  catchError,
  EMPTY,
  finalize,
  forkJoin,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { FeedbackAlertService } from '../../../shared/feedback-alert/service/feedback-alert.service';
import { SharedModule } from '../../../shared/shared.module';
import { isTopLevelDomainFI } from '../../../shared/util/deploy-helper';
import {
  ContactInformation,
  InstallationReportingMduReportContext,
  InstallationReportOverview,
} from '../../entities/installation-reporting-mdu-details.entities';
import { DeliveryStatus } from '../../entities/installation-reporting-mdu.entities';
import { InstallationReportingMduApiService } from '../installation-reporting-mdu-api.service';
import { InstallationReportOverviewTabComponent } from './components/installation-report-overview-tab/installation-report-overview-tab.component';
import { InstallationReportSectionTabComponent } from './components/installation-report-section-tab/installation-report-section-tab.component';
import { InstallationReportTab } from './model/installation-report-tab';
import {
  BuildingSectionsResponse,
  FileMetaV2Response,
  SelfInspectionV2Request,
} from './model/installation-reporting-mdu-bff.entities';
import {
  BUILDING_REPORT_SECTIONS,
  DELIVERY_POINT_REPORT_SECTIONS,
} from './model/installation-reporting-mdu-section-layout.config';
import {
  DeliveryPointReportSectionConfig,
  InstallationReportSectionConfig,
  InstallationReportSectionId,
} from './model/installation-reporting-mdu-section.types';
import {
  loadSelfInspectionDefinitions,
  withDefinitions,
} from './model/self-inspection-definitions.loader';
import { InstallationReportingMduSelfInspectionService } from './service/installation-reporting-mdu-self-inspection.service';
import {
  createDeliveryPointReportSectionStates,
  DeliveryPointReportSectionStates,
  DeliveryPointSectionsResponse,
  mapDeliveryPointReportSectionStatesToUpdateRequest,
} from './state/delivery-point-section-state';
import {
  mergeReloadedSectionStates,
  ReportSectionStates,
  responsesBySection,
  withUploadedSelfInspection,
} from './state/installation-reporting-mdu-section-reload';
import { createBuildingReportSectionStates } from './state/installation-reporting-mdu-section-state';
import { mapBuildingReportSectionStatesToUpdateRequest } from './state/installation-reporting-mdu-section-state.mapper';
import {
  BuildingReportSectionStates,
  SectionNotApplicableChange,
  SectionState,
} from './state/installation-reporting-mdu-section-state.types';

@Component({
  selector: 'app-installation-reporting-mdu-modal',
  imports: [
    GcModalComponent,
    GcDropdownButtonComponent,
    GcListItemComponent,
    GcTabsComponent,
    GcTabComponent,
    IpoTranslatePipe,
    SharedModule,
    InstallationReportOverviewTabComponent,
    InstallationReportSectionTabComponent,
  ],
  templateUrl: './installation-reporting-mdu-modal.component.html',
  styleUrl: './installation-reporting-mdu-modal.component.scss',
})
export class InstallationReportingMduModalComponent
  implements OnInit, OnDestroy
{
  private readonly document = inject(DOCUMENT);
  private readonly api = inject(InstallationReportingMduApiService);
  private readonly languageService = inject(IpoLanguageService);
  private readonly router = inject(Router);
  private readonly selfInspectionService = inject(
    InstallationReportingMduSelfInspectionService,
  );
  private readonly feedbackAlertService = inject(FeedbackAlertService);
  private readonly translate = inject(IpoTranslatePipe);
  private originalBodyOverflow = '';

  report = input.required<InstallationReportingMduReportContext>();
  ifsNumber = input.required<string>();
  groupAgreementId = input.required<string>();

  closed = output<void>();
  saved = output<void>();

  overview = signal<InstallationReportOverview | null>(null);
  sectionStates = signal<ReportSectionStates | null>(null);
  isInitialDataLoaded = signal(false);
  isSaving = signal(false);
  isBomSaving = signal(false);
  readonly isLoading = computed<boolean>(
    () => this.isSaving() || this.isBomSaving() || !this.isInitialDataLoaded(),
  );
  readonly isBom = linkedSignal<boolean>(
    () =>
      this.deliveryPointForOverview()?.deliveryStatus === DeliveryStatus.BOM,
  );
  readonly notApplicableSavingSection =
    signal<InstallationReportSectionId | null>(null);
  private readonly imagePreviewDefinitionId = signal<string | null>(null);
  readonly selfInspectionMutationLoadingState =
    this.selfInspectionService.isMutationLoadingState;
  readonly showImagePreview = signal(false);
  readonly imagePreviewImages = this.selfInspectionService.previewImages;
  readonly isLoadingImagePreview = this.selfInspectionService.previewLoading;
  readonly sections = signal<InstallationReportSectionConfig[]>([]);
  readonly InstallationReportTab = InstallationReportTab;
  readonly activeTab = signal<InstallationReportTab>(
    InstallationReportTab.ORDER_OVERVIEW,
  );
  readonly tabs = computed<InstallationReportTab[]>(() => [
    InstallationReportTab.ORDER_OVERVIEW,
    ...this.sections().map((section) => section.id),
  ]);
  readonly activeTabIndex = computed(() =>
    Math.max(this.tabs().indexOf(this.activeTab()), 0),
  );
  readonly isDeliveryPortalFi = isTopLevelDomainFI();

  constructor() {
    this.syncSectionStatesWithSelfInspectionChanges();
    this.loadReportWhenInputsChange();
  }

  private syncSectionStatesWithSelfInspectionChanges() {
    this.selfInspectionService.uploadSucceeded
      .pipe(takeUntilDestroyed())
      .subscribe(({ definitionId, attachments }) =>
        this.applyUploadedSelfInspection(definitionId, attachments),
      );

    this.selfInspectionService.answerChanged
      .pipe(takeUntilDestroyed())
      .subscribe((definitionId) => this.reloadSections(definitionId));
  }

  private loadReportWhenInputsChange() {
    effect(() => {
      const report = this.report();
      const ifsNumber = this.ifsNumber();
      const groupAgreementId = this.groupAgreementId();
      // Loading must not subscribe the effect to the signals it writes, or a
      // synchronously completing request would re-trigger it endlessly.
      untracked(() => this.loadReport(report, ifsNumber, groupAgreementId));
    });
  }

  private loadReport(
    report: InstallationReportingMduReportContext,
    ifsNumber: string,
    groupAgreementId: string,
  ) {
    const sectionConfigs = this.resetForReport(report);
    if (!ifsNumber || !groupAgreementId) {
      return;
    }

    this.fetchReportData$(report, ifsNumber, groupAgreementId).subscribe(
      ({ sections, definitions, language }) => {
        this.sections.set(
          withDefinitions(sectionConfigs, definitions, language),
        );
        this.applySections(report, sections);
        this.isInitialDataLoaded.set(true);
      },
    );
  }

  private resetForReport(
    report: InstallationReportingMduReportContext,
  ): InstallationReportSectionConfig[] {
    const sectionConfigs = this.baseSections(report.reportType);
    this.overview.set(null);
    this.isInitialDataLoaded.set(false);
    this.sections.set(sectionConfigs);
    this.sectionStates.set(
      this.createInitialSectionStates(report, sectionConfigs),
    );
    return sectionConfigs;
  }

  private fetchReportData$(
    report: InstallationReportingMduReportContext,
    ifsNumber: string,
    groupAgreementId: string,
  ) {
    return this.api
      .getInstallationReportOverview(
        this.reportId(report),
        ifsNumber,
        groupAgreementId,
        report.path,
      )
      .pipe(
        tap((overview) => this.overview.set(overview)),
        switchMap((overview) =>
          forkJoin({
            sections: this.api.getInstallationReportSections(
              overview.installationReportId,
              report.reportType,
            ),
            definitions: loadSelfInspectionDefinitions(
              this.api,
              report.reportType,
            ),
            language: this.languageService.getLanguage$().pipe(take(1)),
          }),
        ),
      );
  }

  ngOnInit() {
    this.originalBodyOverflow = this.document.body.style.overflow;
    this.document.body.style.overflow = 'hidden';
  }

  ngOnDestroy() {
    this.closeImagePreview();
    this.document.body.style.overflow = this.originalBodyOverflow;
  }

  onSave() {
    const overview = this.overview();
    const states = this.sectionStates();
    if (!overview || !states || this.isLoading()) {
      return;
    }

    const request =
      this.report().reportType === 'BUILDING'
        ? mapBuildingReportSectionStatesToUpdateRequest(
            states as BuildingReportSectionStates,
          )
        : mapDeliveryPointReportSectionStatesToUpdateRequest(
            states as DeliveryPointReportSectionStates,
          );
    this.isSaving.set(true);
    this.api
      .updateInstallationReport(overview.installationReportId, request)
      .pipe(
        tap(() => this.saved.emit()),
        switchMap(() =>
          this.api
            .getInstallationReportSections(
              overview.installationReportId,
              this.report().reportType,
            )
            .pipe(catchError(() => EMPTY)),
        ),
        finalize(() => this.isSaving.set(false)),
      )
      .subscribe({
        next: (sections) => {
          this.applySections(this.report(), sections);
        },
        error: () =>
          this.feedbackAlertService.showAlert(
            'critical',
            'installationReportV2.updateInstallationReportError',
          ),
      });
  }

  onSectionNotApplicableChanged(
    sectionId: InstallationReportSectionId,
    event: SectionNotApplicableChange,
  ): void {
    const report = this.report();
    if (report.reportType !== 'BUILDING' || this.notApplicableSavingSection()) {
      return;
    }

    this.notApplicableSavingSection.set(sectionId);
    this.api
      .setSectionNotApplicable({
        sourceId: this.reportId(report),
        groupAgreementId: this.groupAgreementId(),
        sectionType: sectionId,
        notApplicable: event.notApplicable,
        notApplicableComment: event.notApplicable ? event.comment : null,
      })
      .pipe(finalize(() => this.notApplicableSavingSection.set(null)))
      .subscribe({
        next: () => this.reloadSections(),
        error: () =>
          this.feedbackAlertService.showAlert(
            'critical',
            'buildingPageModal.sectionNotApplicable.saveError',
          ),
      });
  }

  onPrimaryButtonClicked(): void {
    if (this.isBom()) {
      this.onUndoBom();
      return;
    }
    this.onSave();
  }

  onBomMarked(comment: string): void {
    this.updateBom(true, comment);
  }

  onUndoBom(): void {
    this.updateBom(false);
  }

  closeModal() {
    this.closed.emit();
  }

  @HostListener('document:keydown.escape')
  onEscapeKey() {
    this.closeModal();
  }

  openSigningPage() {
    const building = this.buildingForOverview();
    if (!building) {
      return;
    }
    const url = this.router.serializeUrl(
      this.router.createUrlTree([
        '/installation-reporting',
        this.ifsNumber(),
        this.groupAgreementId(),
        building.id,
        'sign',
      ]),
    );
    window.open(url, '_blank');
  }

  onSectionStateChanged(
    sectionId: InstallationReportSectionId,
    state: SectionState,
  ) {
    this.sectionStates.update((states) =>
      states ? { ...states, [sectionId]: state } : states,
    );
  }

  onSectionDateChanged(
    sectionId: InstallationReportSectionId,
    event: {
      field: 'plannedDate' | 'completedDate';
      value: Date | undefined;
    },
  ): void {
    this.onSave();
  }

  onActiveTabChanged(index: number) {
    const tab = this.tabs()[index];
    if (tab) {
      this.activeTab.set(tab);
    }
  }

  onDropdownItemClicked(itemId: string) {
    const tab = this.tabs().find((tabId) => tabId === itemId);
    if (tab) {
      this.activeTab.set(tab);
    }
  }

  navigateToPreinstallation() {
    this.activeTab.set(InstallationReportTab.PRE_INSTALLATION);
  }

  preInstallationMacAddress(): string | undefined {
    return this.sectionStates()?.PRE_INSTALLATION?.macAddress ?? undefined;
  }

  onSelfInspectionCompleted(definitionId: string) {
    this.persistSelfInspection(definitionId);
  }

  onSelfInspectionMarkedNotApplicable(event: {
    itemId: string;
    comment: string;
  }) {
    this.persistSelfInspection(event.itemId, event.comment);
  }

  onSelfInspectionUndone(definitionId: string) {
    const overview = this.overview();
    if (!overview) {
      return;
    }
    this.api
      .deleteSelfInspection(overview.installationReportId, definitionId)
      .subscribe({
        next: () => this.reloadSections(),
        error: () => this.reloadSections(),
      });
  }

  onSelfInspectionFilesSelected(event: { itemId: string; files: File[] }) {
    const overview = this.overview();
    if (!overview) {
      return;
    }
    this.selfInspectionService.transformAndUploadSelfInspectionImages(
      event.files,
      event.itemId,
      overview.installationReportId,
    );
  }

  onSelfInspectionAttachmentsViewed(event: {
    itemId: string;
    attachments: FileMetaV2Response[];
  }) {
    this.imagePreviewDefinitionId.set(event.itemId);
    this.selfInspectionService.setPreviewImages(event.attachments);
    this.showImagePreview.set(true);
  }

  closeImagePreview() {
    this.imagePreviewDefinitionId.set(null);
    this.selfInspectionService.clearPreviewImages();
    this.showImagePreview.set(false);
  }

  uploadImagesFromPreview($event: Event) {
    const overview = this.overview();
    const definitionId = this.imagePreviewDefinitionId();
    const files = Array.from(($event.target as HTMLInputElement).files ?? []);
    if (!overview || !definitionId) {
      return;
    }
    return this.selfInspectionService.transformAndUploadSelfInspectionImages(
      files,
      definitionId,
      overview.installationReportId,
    );
  }

  deleteImageFromPreview(fileId: string) {
    const overview = this.overview();
    const definitionId = this.imagePreviewDefinitionId();
    if (!overview || !definitionId) {
      return;
    }
    return this.selfInspectionService.deleteSelfInspectionImage(
      overview.installationReportId,
      { id: definitionId },
      fileId,
    );
  }

  downloadImages(files: { fileId: string; fileName: string }[]) {
    this.selfInspectionService.downloadImages(files);
  }

  buildingForOverview() {
    const report = this.report();
    return report.reportType === 'BUILDING' ? report.building : null;
  }

  deliveryPointForOverview() {
    const report = this.report();
    return report.reportType === 'DELIVERY_POINT' ? report.deliveryPoint : null;
  }

  contact(): ContactInformation | null {
    return this.buildingForOverview()?.contactInformation[0] ?? null;
  }

  address(): string {
    const report = this.report();
    return report.reportType === 'BUILDING'
      ? report.building.address
      : report.deliveryPoint.address;
  }

  private baseSections(
    reportType: InstallationReportingMduReportContext['reportType'],
  ) {
    const sections =
      reportType === 'BUILDING'
        ? BUILDING_REPORT_SECTIONS
        : DELIVERY_POINT_REPORT_SECTIONS;
    return sections.map((section) => ({ ...section, items: [] }));
  }

  private reportId(report: InstallationReportingMduReportContext) {
    return report.reportType === 'BUILDING'
      ? report.building.id
      : report.deliveryPoint.id;
  }

  private createInitialSectionStates(
    report: InstallationReportingMduReportContext,
    sections: InstallationReportSectionConfig[],
  ): ReportSectionStates {
    return report.reportType === 'BUILDING'
      ? createBuildingReportSectionStates(report.building, sections)
      : createDeliveryPointReportSectionStates(
          report.deliveryPoint,
          sections as DeliveryPointReportSectionConfig[],
        );
  }

  private applyUploadedSelfInspection(
    definitionId: string,
    attachments: FileMetaV2Response[],
  ): void {
    const updated = withUploadedSelfInspection(
      this.sections(),
      this.sectionStates(),
      definitionId,
      attachments,
    );
    if (updated) {
      this.sectionStates.set(updated);
    }
  }

  private reloadSections(definitionId?: string) {
    const overview = this.overview();
    if (!overview) {
      return;
    }
    this.api
      .getInstallationReportSections(
        overview.installationReportId,
        this.report().reportType,
      )
      .pipe(
        finalize(() => {
          if (definitionId) {
            this.selfInspectionService.setMutationLoadingStateByDefinitionId(
              false,
              definitionId,
            );
          }
        }),
      )
      .subscribe((sections) =>
        this.applySections(this.report(), sections, definitionId),
      );
  }

  private applySections(
    report: InstallationReportingMduReportContext,
    sections: BuildingSectionsResponse | DeliveryPointSectionsResponse,
    focusDefinitionId?: string,
  ) {
    const reportSections = this.sections();
    this.sectionStates.set(
      mergeReloadedSectionStates(
        reportSections,
        responsesBySection(report.reportType, sections),
        this.sectionStates(),
        this.createInitialSectionStates(report, reportSections),
        focusDefinitionId,
      ),
    );
  }

  private updateBom(markBom: boolean, comment: string | null = null): void {
    const report = this.report();
    if (report.reportType !== 'DELIVERY_POINT' || this.isLoading()) {
      return;
    }

    this.isBomSaving.set(true);
    this.api
      .markDeliveryPointBom({
        deliveryPointId: report.deliveryPoint.id,
        groupAgreementId: this.groupAgreementId(),
        markBom,
        bomComment: comment,
      })
      .pipe(
        switchMap(() =>
          this.api
            .getInstallationReportOverview(
              report.deliveryPoint.id,
              this.ifsNumber(),
              this.groupAgreementId(),
              report.path,
            )
            .pipe(catchError(() => of(this.overview()))),
        ),
        finalize(() => this.isBomSaving.set(false)),
      )
      .subscribe({
        next: (overview) => {
          this.overview.set(overview);
          this.isBom.set(markBom);
          if (markBom) {
            this.activeTab.set(InstallationReportTab.ORDER_OVERVIEW);
          }
          this.saved.emit();
        },
        error: () =>
          this.feedbackAlertService.showAlert(
            'critical',
            markBom
              ? this.translate.transform(
                  'installationReportV2.orderOverviewTab.markAsBomError',
                )
              : this.translate.transform(
                  'installationReportV2.orderOverviewTab.undoBomError',
                ),
          ),
      });
  }

  private persistSelfInspection(
    definitionId: string,
    notApplicableComment: string | null = null,
  ) {
    const overview = this.overview();
    if (!overview) {
      return;
    }

    const request: SelfInspectionV2Request = {
      installationReportId: overview.installationReportId,
      definitionId,
      completed: true,
      isNotCompletable: notApplicableComment !== null,
      isNotCompletableComment: notApplicableComment,
    };
    this.api
      .completeSelfInspection(
        overview.installationReportId,
        definitionId,
        request,
      )
      .subscribe({
        next: () => this.reloadSections(),
        error: () => this.reloadSections(),
      });
  }
}
